<script setup>
import TheSelectAdAccounts from '@shared-components/ui/selects/TheSelectAdAccounts.vue'
import {defineEmits, ref, computed, defineExpose, defineProps, onMounted, nextTick, defineModel} from 'vue'
import {useAdAccountTopUpRequestStore} from '@/views/ad-account-requests/top-up/AdAccountTopUpRequestStore'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import {notify} from '@kyvg/vue3-notification'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import TheSwitcher from '@shared-components/ui/inputs/TheSwitcher.vue'
import CompanyBalanceErrorAlert from '@/views/ad-account-requests/top-up/components/CompanyBalanceErrorAlert.vue'
import DisabledAccountErrorAlert from '@shared-components/pages/requests/manage-funds/top-up/DisabledAccountErrorAlert.vue'
import {useUserStore} from '@/stores/UserStore'
import AlertInfoSimple from '@shared-components/ui/alerts/AlertInfoSimple.vue'
import {formatSum} from '@shared/utils/helpers'
import dayjs from 'dayjs'
import ActiveTopUpRequestsList from '@shared-components/pages/requests/manage-funds/top-up/ActiveTopUpRequestsList.vue'

const {createAdAccountTopUpRequest, getAdAccountTopUpRequests} = useAdAccountTopUpRequestStore()
const {canUseTopUpAsap} = storeToRefs(useAuthenticationStore())
const {getUserBalance} = useUserStore()
const {pingBackend} = useDefaultStore()

const companyModelValue = defineModel('company', {type: String, required: true})

// Emits & props
let emit = defineEmits(['created', 'open-invoice-modal'])
const props = defineProps({
  adAccount: {
    type: Object,
  },
  isUserCompanyHash: {
    type: Boolean,
    default: false,
  },
})

// Refs
let _uid = 0
const defaultFormData = {
  entity_id: null,
  entity_type: null,
  amount: null,
  is_asap: false,
  is_withdraw: false,
  is_disabled: false,
  top_up_unavailable_reasons: [],
}
const makeItem = () => ({...cloneSafe(defaultFormData), _uid: ++_uid})
const formData = ref([makeItem()])
const selectAdAccountRef = ref()
const error = ref(null)
const loading = ref(false)
const balanceLoading = ref(false)
const balanceInfo = ref(null)
const activeTopUpsList = ref()

// Computed
const canSubmit = computed(() => {
  return formData.value.every(
    item => item.entity_id && item.amount !== null && item.amount !== '' && item.is_disabled === false,
  )
})
const isCompanyBalanceError = computed(() => error.value?.errors?.company_balances?.length > 0)

// Methods
onMounted(() => {
  init()
})
const init = async () => {
  if (props.isUserCompanyHash) {
    await getBalanceInfo()
    nextTick(() => {
      activeTopUpsList?.value?.handleGetTopUpRequests()
    })
  }
}
const getBalanceInfo = async () => {
  balanceLoading.value = true

  try {
    balanceInfo.value = await getUserBalance()
  } catch (error) {
    notify({type: 'error', text: error?.data?.message})
  } finally {
    balanceLoading.value = false
  }
}
const removeItem = index => {
  formData.value.splice(index, 1)
}
const setIsOptionDisabled = (option, index) => {
  formData.value[index].is_disabled = option.is_disabled
  formData.value[index].top_up_unavailable_reasons = option.top_up_unavailable_reasons ?? []
}
const onAdAccountChanged = (adAccount, index) => {
  formData.value[index].entity_type = adAccount?.entity_type
  formData.value[index].amount = null
}
const getRequestParams = () => {
  return cloneSafe(formData.value).map(item => {
    delete item._uid
    delete item.is_disabled
    delete item.top_up_unavailable_reasons
    return {
      ...item,
      amount: item.amount,
    }
  })
}
const handleCreate = async () => {
  loading.value = true

  try {
    await createAdAccountTopUpRequest(getRequestParams())

    pingBackend()
    emit('created')
    notify({type: 'success', text: 'Top-up request submitted'})
  } catch (responseError) {
    error.value = responseError.data
    throw responseError
  } finally {
    loading.value = false
  }
}
const addNewItem = () => {
  formData.value.push(makeItem())

  nextTick(() => {
    selectAdAccountRef.value?.[formData.value.length - 1]?.handleSearch?.('  ')
    activeTopUpsList?.value?.handleGetTopUpRequests()
  })
}
const openInvoiceModal = () => {
  emit('open-invoice-modal')
}
const resetFormAndReloadAccounts = () => {
  formData.value = [makeItem()]

  for (let i = 0; i < selectAdAccountRef.value.length; i++) {
    nextTick(() => {
      selectAdAccountRef.value[i].handleSearch?.('  ')
    })
  }
}

defineExpose({handleCreate, canSubmit, resetFormAndReloadAccounts})
</script>

<template>
  <alert-info-simple v-if="balanceInfo?.show_available_funds" class="custom mb-5 fs-13-px">
    Estimated available balance for top-ups: {{ formatSum(balanceInfo?.balance_info?.available_funds) }}
  </alert-info-simple>
  <div class="alert alert-grey-with-border custom mb-4 py-5" v-for="(item, index) in formData" :key="item._uid">
    <div class="row">
      <div class="col">
        <label class="custom fs-4 lh-1" :for="`top-up-account-${index}`">Ad Account *</label>
        <the-select-ad-accounts
          ref="selectAdAccountRef"
          v-model="item.entity_id"
          :list-by-search="isUserCompanyHash"
          :payload="{with_can_has_top_up: 1, company_hash: companyModelValue, purpose: 'topup'}"
          @changed="onAdAccountChanged($event, index)"
          @select-option="setIsOptionDisabled($event, index)"
          :disabled="loading"
          :id="`top-up-account-${index}`"
          placeholder="Select Ad Account"
          :preselect-account="index === 0 ? props.adAccount : null"
          required
        />
      </div>
      <div class="col-auto w-180-px pl-0">
        <label class="custom fs-4 lh-1" :for="`top-up-amount-${index}`">Amount *</label>
        <div class="input-group">
          <span class="input-group-text">$</span>
          <input
            v-model="item.amount"
            :class="['form-control', isCompanyBalanceError ? 'is-invalid' : '']"
            type="number"
            step="0.01"
            :id="`top-up-amount-${index}`"
            placeholder="Enter Amount"
            :disabled="loading"
            required
          />
        </div>
      </div>
      <div v-if="canUseTopUpAsap" class="col-auto d-flex align-items-end pl-0">
        <the-switcher
          v-model="item.is_asap"
          label="ASAP"
          :id="`top-up-${index}-asap`"
          :disabled="loading"
          class="mb-1"
        />
      </div>
      <div v-if="formData?.length > 1" class="col-auto d-flex align-items-end pl-0">
        <div class="input-height">
          <button class="form-icon-btn h-100" type="button" :disabled="loading" @click="removeItem(index)">
            <span class="fa-light fa-trash text-muted" />
          </button>
        </div>
      </div>
    </div>
    <disabled-account-error-alert
      v-if="item.is_disabled"
      :reasons="item.top_up_unavailable_reasons"
      class="custom mt-4"
    />
  </div>
  <button @click="addNewItem" class="btn btn-secondary btn-sm fs-14-px" type="button" :disabled="loading">
    Add Another Account
  </button>
  <active-top-up-requests-list
    v-if="companyModelValue"
    ref="activeTopUpsList"
    :today-date="dayjs().format('YYYY-MM-DD')"
    :company-hash="companyModelValue"
    :get-requests="getAdAccountTopUpRequests"
    class="custom mt-4"
  />
  <company-balance-error-alert v-if="isCompanyBalanceError" @open-invoice-modal="openInvoiceModal" class="my-3" />
  <api-errors v-if="error && !isCompanyBalanceError" :error="error" class="mt-3"></api-errors>
</template>
